/**
 * Theme cookie bar driven by Shopify’s Customer Privacy API.
 *
 * Shopify still injects #shopify-pc__banner; CSS hides it. This bar is the
 * visible chrome. Choices are written through setTrackingConsent and also
 * stored in localStorage so the bar stays dismissed when the Storefront
 * consent POST fails (theme preview on http://127.0.0.1).
 *
 * The bar only opens for visitors Shopify places in a consent-banner region,
 * and never for US visitors. It stays hidden until that check returns, and
 * stays hidden if the API never loads.
 *
 * The preferences modal is Shopify’s. We bind its buttons because their
 * handlers wait on that same POST and never close when it errors.
 */
(() => {
  const root = document.getElementById("cookie-banner");
  if (!root) return;

  const STORAGE_KEY = "moses-cookie-consent";

  const NATIVE = {
    accept: "shopify-pc__banner__btn-accept",
    reject: "shopify-pc__banner__btn-decline",
    prefs: "shopify-pc__banner__btn-manage-prefs",
  };

  const PREFS = {
    root: "shopify-pc__prefs",
    close: "shopify-pc__prefs__header-close",
    overlay: "shopify-pc__prefs__overlay",
    accept: "shopify-pc__prefs__header-accept",
    decline: "shopify-pc__prefs__header-decline",
    save: "shopify-pc__prefs__header-save",
    preferences: "shopify-pc__prefs__preferences-input",
    marketing: "shopify-pc__prefs__marketing-input",
    analytics: "shopify-pc__prefs__analytics-input",
  };

  const consentRecorded = (consent) =>
    ["analytics", "marketing", "preferences"].some(
      (key) => consent?.[key] === "yes" || consent?.[key] === "no",
    );

  const loadStored = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const saveStored = (consent) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    } catch {
      /* private mode */
    }
  };

  let hiding = false;

  const show = () => {
    hiding = false;
    root.classList.remove("pointer-events-none", "grid-rows-[0fr]", "opacity-0");
    root.classList.add("grid-rows-[1fr]", "opacity-100");
    root.hidden = false;
  };

  const hide = ({ animate = false } = {}) => {
    if (root.hidden || hiding) return;

    if (!animate) {
      root.hidden = true;
      return;
    }

    hiding = true;
    root.classList.add("pointer-events-none", "grid-rows-[0fr]", "opacity-0");
    root.classList.remove("grid-rows-[1fr]", "opacity-100");

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      root.hidden = true;
      hiding = false;
      root.removeEventListener("transitionend", onEnd);
    };
    const onEnd = (event) => {
      if (event.target !== root) return;
      finish();
    };
    root.addEventListener("transitionend", onEnd);
    setTimeout(finish, 250);
  };

  const currentConsent = (api) =>
    typeof api?.currentVisitorConsent === "function"
      ? api.currentVisitorConsent()
      : null;

  const regionRequiresBanner = (api) => {
    const region =
      typeof api?.getRegion === "function" ? String(api.getRegion()) : "";
    if (region.toUpperCase().startsWith("US")) return false;

    if (typeof api?.shouldShowBanner === "function") {
      return Boolean(api.shouldShowBanner());
    }
    if (typeof api?.shouldShowGDPRBanner === "function") {
      return Boolean(api.shouldShowGDPRBanner());
    }
    return false;
  };

  const applyConsent = (consent) => {
    saveStored(consent);
    window.Shopify?.customerPrivacy?.setTrackingConsent?.(
      {
        analytics: Boolean(consent.analytics),
        marketing: Boolean(consent.marketing),
        preferences: Boolean(consent.preferences),
        sale_of_data: Boolean(consent.sale_of_data ?? consent.marketing),
      },
      () => {},
    );
  };

  const grantedConsent = (granted) => ({
    analytics: granted,
    marketing: granted,
    preferences: granted,
    sale_of_data: granted,
  });

  const sync = (api) => {
    const stored = loadStored();
    if (consentRecorded(currentConsent(api)) || stored) {
      hide();
      if (stored && !consentRecorded(currentConsent(api))) applyConsent(stored);
      return;
    }
    if (regionRequiresBanner(api)) show();
    else hide();
  };

  const clickNative = (id) => {
    const el = document.getElementById(id);
    if (!el) return false;
    el.click();
    return true;
  };

  const closePreferences = () => {
    const prefs = document.getElementById(PREFS.root);
    if (!prefs) return;
    prefs.style.setProperty("display", "none", "important");
    document.body.style.removeProperty("overflow");
    document.documentElement.style.removeProperty("overflow");
  };

  const bindPreferences = () => {
    const prefs = document.getElementById(PREFS.root);
    if (!prefs || prefs.dataset.mosesBound === "true") return;
    prefs.dataset.mosesBound = "true";

    const onClose = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      closePreferences();
    };

    const onAccept = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      applyConsent(grantedConsent(true));
      closePreferences();
      hide({ animate: true });
    };

    const onDecline = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      applyConsent(grantedConsent(false));
      closePreferences();
      hide({ animate: true });
    };

    const onSave = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const marketing = Boolean(
        document.getElementById(PREFS.marketing)?.checked,
      );
      applyConsent({
        preferences: Boolean(
          document.getElementById(PREFS.preferences)?.checked,
        ),
        marketing,
        analytics: Boolean(document.getElementById(PREFS.analytics)?.checked),
        sale_of_data: marketing,
      });
      closePreferences();
      hide({ animate: true });
    };

    document
      .getElementById(PREFS.close)
      ?.addEventListener("click", onClose, true);
    document
      .getElementById(PREFS.overlay)
      ?.addEventListener("click", onClose, true);
    document
      .getElementById(PREFS.accept)
      ?.addEventListener("click", onAccept, true);
    document
      .getElementById(PREFS.decline)
      ?.addEventListener("click", onDecline, true);
    document.getElementById(PREFS.save)?.addEventListener("click", onSave, true);
  };

  const loadPrivacy = () =>
    new Promise((resolve, reject) => {
      const existing = window.Shopify?.customerPrivacy;
      if (existing && typeof existing.setTrackingConsent === "function") {
        resolve(existing);
        return;
      }

      if (typeof window.Shopify?.loadFeatures !== "function") {
        reject(new Error("Shopify.loadFeatures unavailable"));
        return;
      }

      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        (error) => {
          if (error) {
            reject(error);
            return;
          }
          const api = window.Shopify.customerPrivacy;
          if (!api || typeof api.setTrackingConsent !== "function") {
            reject(new Error("Customer Privacy API unavailable"));
            return;
          }
          resolve(api);
        },
      );
    });

  const setConsent = (granted) => {
    applyConsent(grantedConsent(granted));
    hide({ animate: true });
    clickNative(granted ? NATIVE.accept : NATIVE.reject);
  };

  const openPreferences = () => {
    const prefs = document.getElementById(PREFS.root);
    prefs?.style.removeProperty("display");
    bindPreferences();

    if (clickNative(NATIVE.prefs)) return;
    if (typeof window.privacyBanner?.showPreferences === "function") {
      window.privacyBanner.showPreferences()?.then?.(() => bindPreferences());
      bindPreferences();
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      bindPreferences();
      if (clickNative(NATIVE.prefs) || Date.now() - started > 3000) {
        clearInterval(timer);
      }
    }, 50);
  };

  root
    .querySelector("[data-cookie-accept]")
    ?.addEventListener("click", () => setConsent(true));
  root
    .querySelector("[data-cookie-reject]")
    ?.addEventListener("click", () => setConsent(false));
  root.querySelector("[data-cookie-prefs]")?.addEventListener("click", (event) => {
    event.preventDefault();
    openPreferences();
  });

  new MutationObserver(() => bindPreferences()).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  bindPreferences();

  if (loadStored()) hide();

  loadPrivacy()
    .then((api) => {
      sync(api);
      document.addEventListener("visitorConsentCollected", () => sync(api));
    })
    .catch(() => {
      hide();
    });
})();
